/**
 * 需求来源：docs/合作页、CJ Entity、Ads开户落地页3个/AI提示词.md（第2次提交 任务1）、advertisingservice.html。
 * Section 5：cj-account-setup 痛点(4 barrier 卡) + 6 步流程 timeline。
 * 主 CTA「Talk to an Account Specialist」打开工单弹窗。
 * 注意事项：文案（eyebrow/title/desc/barriers/steps）经 ConstantsContext 取词，配色与 SVG 图标为视觉资产保留在组件内。
 */
import React, { useContext } from 'react';
import TicketContext from './TicketContext';
import ConstantsContext from './ConstantsContext';
import { mix } from '../colorMix';

// barrier 视觉资产（配色 + 图标）顺序与 ACCOUNT_SETUP.barriers 一一对应
var BARRIER_VISUALS = [
  {
    accent: '#4f7cff', soft: 'rgba(79,124,255,.20)',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3 5 6v5c0 4.4 2.8 8.4 7 10 4.2-1.6 7-5.6 7-10V6l-7-3Z" /><path d="M9.5 11.5 11 13l3.5-3.5" /></svg>),
  },
  {
    accent: '#8b5cf6', soft: 'rgba(139,92,246,.20)',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.2 2.5 3.2 5.5 3.2 9S14.2 18.5 12 21M12 3C9.8 5.5 8.8 8.5 8.8 12S9.8 18.5 12 21" /></svg>),
  },
  {
    accent: '#31b7d8', soft: 'rgba(49,183,216,.20)',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M3 10h18M7 15h4" /></svg>),
  },
  {
    accent: '#ff914d', soft: 'rgba(255,145,77,.20)',
    icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 19V9M12 19V5M19 19v-7" /><path d="m4 7 5-4 4 3 7-4" /></svg>),
  },
];

// step 视觉资产（配色 + 序号）顺序与 ACCOUNT_SETUP.steps 一一对应
var STEP_VISUALS = [
  { step: '#4f7cff', num: '01' },
  { step: '#745ff0', num: '02' },
  { step: '#9a5ee7', num: '03' },
  { step: '#31b7d8', num: '04' },
  { step: '#28b487', num: '05' },
  { step: '#ff914d', num: '06' },
];

// barrier 卡：由 accent 主色预算派生 CSS 变量（等价原 less 的 color-mix）
function barrierVars(accent, soft) {
  if (!accent) return { '--soft': soft };
  return {
    '--accent': accent,
    '--soft': soft,
    '--accent-hover-bd': mix(accent, '#dfe5ee', 30),   // hover 描边
    '--accent-icon-bd': mix(accent, 'transparent', 24), // 图标描边
    '--accent-icon-bg': mix(accent, '#ffffff', 10),     // 图标浅底
  };
}

// process 卡：由 step 主色预算派生 CSS 变量（等价原 less 的 color-mix）
function stepVars(step) {
  if (!step) return {};
  return {
    '--step': step,
    '--step-hover-bd': mix(step, '#dfe5ed', 28),  // hover 描边
    '--step-lite': mix(step, '#ffffff', 70),      // 序号圆渐变浅端
    '--step-glow': mix(step, 'transparent', 24),  // 序号圆投影
    '--step-label': mix(step, '#273141', 78),     // label 文字（压暗）
  };
}

function AccountSetup() {
  const openTicket = useContext(TicketContext);
  const { ACCOUNT_SETUP } = useContext(ConstantsContext);
  return (
    <section className="cj-account-setup">
      <div className="cj-account-setup__container">
        <div className="cj-account-setup__hero">
          <div className="cj-account-setup__intro">
            <div className="cj-account-setup__eyebrow">{ACCOUNT_SETUP.eyebrow}</div>
            <h2 className="cj-account-setup__title">
              {ACCOUNT_SETUP.title}
              <span className="accent"> {ACCOUNT_SETUP.titleAccent}</span>
            </h2>
            <p className="cj-account-setup__description">{ACCOUNT_SETUP.description}</p>
            <a className="cj-account-setup__cta" href="#contact-account-specialist" onClick={(e) => { e.preventDefault(); openTicket(); }}>
              {ACCOUNT_SETUP.cta}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </a>
          </div>

          <div className="cj-account-setup__barrier-grid">
            {ACCOUNT_SETUP.barriers.map((b, i) => {
              var v = BARRIER_VISUALS[i] || {};
              return (
                <article className="cj-barrier-card" key={b.h3} style={barrierVars(v.accent, v.soft)}>
                  <div className="cj-barrier-card__icon">{v.icon}</div>
                  <h3>{b.h3}</h3>
                  <p>{b.p}</p>
                </article>
              );
            })}
          </div>
        </div>

        <div className="cj-account-process">
          <div className="cj-account-process__header">
            <div>
              <div className="cj-account-process__eyebrow">{ACCOUNT_SETUP.processEyebrow}</div>
              <h2 className="cj-account-process__title">{ACCOUNT_SETUP.processTitle}</h2>
            </div>
            <p className="cj-account-process__intro">{ACCOUNT_SETUP.processIntro}</p>
          </div>

          <div className="cj-account-process__timeline">
            {ACCOUNT_SETUP.steps.map((s, i) => {
              var v = STEP_VISUALS[i] || {};
              return (
                <article className="cj-process-card" key={v.num} style={stepVars(v.step)}>
                  <div className="cj-process-card__number">{v.num}</div>
                  <div className="cj-process-card__label">{s.label}</div>
                  <h3>{s.h3}</h3>
                  <p>{s.p}</p>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default AccountSetup;
